/**
 * Minimal JWT helpers. We only need to read the `exp` claim to schedule
 * refreshes — full verification happens server-side on care-management-gql.
 */

interface JwtClaims {
  exp?: number;
  /** Standard JWT subject claim. PP-API's `UserValidationHelper` validates
   *  the `userId` request param against the bearer's `sub` claim (NOT
   *  `oid`) — so this is the primary source for the native module's
   *  `userId` prop; see `ppUserIdFromClaims` below. Also used as the
   *  OID fallback for B2C tokens that don't carry `oid`. */
  sub?: string;
  /** Entra v1 object id claim — present on Entra tokens, absent on B2C.
   *  PP-API does not accept it in place of `sub` for userId validation, so
   *  it's kept only as a fallback for bearers that (unexpectedly) carry no
   *  `sub`. */
  oid?: string;
  /** Rare alternate spellings some IdPs use for the object id. Checked only
   *  as a last-resort fallback, never preferred over `sub`. */
  objectId?: string;
  /** Provider Portal's B2C custom attribute for the signed-in user's PP role.
   *  Used (Create-Auth-V2 native, ACT-1247 Phase 4) to source the native
   *  module's `userRole` prop — see ppUserRoleFromClaims below. Only present
   *  on an actual PP B2C bearer; an Entra/Pathways bearer carries no PP role
   *  claim at all. */
  extension_Role?: string;
  /** Generic role-claim fallbacks some IdPs use. PP is not known to emit
   *  these, but they're cheap to check if extension_Role is absent. */
  roles?: string[] | string;
  role?: string;
}

function base64UrlDecode(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    return atob(padded);
  } catch {
    return null;
  }
}

export function decodeJwtClaims(token: string): JwtClaims | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const payload = base64UrlDecode(parts[1]);
  if (payload === null) return null;
  try {
    return JSON.parse(payload) as JwtClaims;
  } catch {
    return null;
  }
}

/** Returns the `exp` claim in epoch milliseconds, or null if missing/malformed. */
export function jwtExpiryMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const claims = decodeJwtClaims(token);
  if (!claims?.exp) return null;
  return claims.exp * 1000;
}

/**
 * Returns the user's stable OID from a JWT.
 * Prefers the Entra `oid` claim; falls back to the standard `sub` claim for
 * B2C tokens that don't carry `oid`. Returns null when the token is absent or
 * unparseable — callers should treat null as "unauthenticated".
 */
export function jwtUserOid(
  token: string | null | undefined,
): string | null {
  if (!token) return null;
  const claims = decodeJwtClaims(token);
  return claims?.oid ?? claims?.sub ?? null;
}

/** True if the token is within `withinMs` of expiring (or already has). */
export function isJwtExpiringSoon(
  token: string | null | undefined,
  withinMs: number,
  now: number = Date.now(),
): boolean {
  const expMs = jwtExpiryMs(token);
  if (expMs === null) return false;
  return expMs - now <= withinMs;
}

/**
 * Best-effort PP role extraction from whichever role claim the bearer
 * carries. PP's own B2C custom attribute is `extension_Role`; `roles`/`role`
 * are checked only as generic fallbacks other IdPs use (PP is not known to
 * emit them). Returns null if none are present — e.g. the active bearer is
 * an Entra/Pathways token with NO PP role claim at all, not just an empty
 * value. Callers decide the fallback default (see MemberProfilePage.tsx's
 * nativeUserRole).
 */
export function ppUserRoleFromClaims(claims: JwtClaims | null): string | null {
  if (!claims) return null;
  if (claims.extension_Role) return claims.extension_Role;
  if (Array.isArray(claims.roles) && claims.roles.length > 0) return claims.roles[0];
  if (typeof claims.roles === "string" && claims.roles) return claims.roles;
  if (claims.role) return claims.role;
  return null;
}

/**
 * PP userId extraction for the native Create-Auth module. PP-API's
 * `UserValidationHelper` validates the `userId` request param against the
 * bearer's **`sub`** claim, not `oid` — so `sub` must be preferred here even
 * though `oid` is the more common "stable object id" claim elsewhere in this
 * file. Falls back to `oid`/`objectId` only for bearers that (unexpectedly)
 * carry no `sub` at all.
 *
 * DEV-ONLY diagnostic: if every candidate is empty, warns with the decoded
 * claim key set so the real shape of a misbehaving bearer is visible in the
 * browser console without needing to attach a debugger.
 */
export function ppUserIdFromClaims(claims: JwtClaims | null): string | undefined {
  if (!claims) return undefined;
  const userId = claims.sub || claims.oid || claims.objectId || undefined;
  if (!userId && import.meta.env.DEV) {
    console.warn(
      "[create-auth-native] userId claim empty; decoded claim keys:",
      Object.keys(claims),
    );
  }
  return userId;
}
